/**
 * FloViewOverlay — 공급 흐름도의 고정 거터(레인 이름)와 고정 눈금자(열 번호)를 그린다.
 * FloCanvas 가 변환(pan·zoom)되는 내용 박스 위에 얹는다.
 * 세로(거터)는 view.y·scale 만, 가로(눈금자)는 view.x·scale 만 따라간다 — 그래서 각자 고정된다.
 * 글자 크기는 확대/축소와 무관하게 항상 같다.
 * 팬·줌 매 프레임마다 다시 그려지므로 스타일은 모두 인라인 style 로 준다 —
 * 스타일 엔진이 프레임마다 클래스를 직렬화하지 않고, 정적 렌더에서도 같은 모양이 나온다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import { useTranslation } from "react-i18next";

import { BOX_W } from "@plannel/components/flo/floGeometry";
import {
  FLO_COLORS, FLO_GUTTER_W, FLO_RULER_H, LANE_TYPE_LABEL_KEY, typeColor,
} from "@plannel/components/flo/floTokens";

const HAIRLINE = `1px solid ${FLO_COLORS.laneBorder}`;
const CLIP_TEXT = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** 열 번호 — 1부터 시작해 2자리로 채운다("01","02",…), 화면 x = view.x + 열 중심*scale. */
function RulerLabels({ view, geometry, font }) {
  const cols = [];
  for (let c = 0; c <= geometry.maxCol; c += 1) cols.push(c);
  return cols.map((c) => {
    const cx = geometry.colX0(c) + BOX_W / 2;
    // 눈금자 띠가 거터 폭만큼 오른쪽에서 시작하므로 띠 안 좌표로 옮긴다.
    const left = view.x + cx * view.scale - FLO_GUTTER_W;
    return (
      <div key={c} data-flo-ruler-col={c}
           style={{
             position: "absolute", top: 0, bottom: 0, left, transform: "translateX(-50%)",
             display: "flex", alignItems: "center", font, color: FLO_COLORS.text2, whiteSpace: "nowrap",
           }}>
        {String(c + 1).padStart(2, "0")}
      </div>
    );
  });
}

/** 레인 셀 하나 — 이름(굵게)·유형 부제 두 줄, 아래쪽에 헤어라인을 그어 다음 셀과 가른다. */
function GutterLane({ lane, view, t, fonts }) {
  // 거터가 눈금자 높이만큼 아래에서 시작하므로 거터 안 좌표로 옮긴다.
  const top = view.y + lane.top * view.scale - FLO_RULER_H;
  const height = lane.height * view.scale;
  const labelKey = LANE_TYPE_LABEL_KEY[lane.type];
  const subtitle = labelKey ? t(labelKey) : (lane.type || "");
  return (
    <div data-flo-gutter-lane={lane.k}
         style={{
           position: "absolute", left: 0, right: 0, top, height,
           overflow: "hidden", borderBottom: HAIRLINE, boxSizing: "border-box",
           display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 8px",
         }}>
      <div style={{ font: fonts?.labelFont, color: FLO_COLORS.text1, ...CLIP_TEXT }}>{lane.label}</div>
      <div style={{ font: fonts?.codeFont, color: typeColor(lane.type), ...CLIP_TEXT }}>{subtitle}</div>
    </div>
  );
}

export default function FloViewOverlay({ view, geometry, fonts }) {
  const { t } = useTranslation();
  return (
    <>
      <div data-flo-ruler
           style={{
             position: "absolute", left: FLO_GUTTER_W, top: 0, right: 0, height: FLO_RULER_H,
             overflow: "hidden", background: FLO_COLORS.canvasBg, borderBottom: HAIRLINE,
             boxSizing: "border-box",
           }}>
        <RulerLabels view={view} geometry={geometry} font={fonts?.codeFont} />
      </div>
      <div data-flo-gutter
           style={{
             position: "absolute", left: 0, top: FLO_RULER_H, width: FLO_GUTTER_W, bottom: 0,
             overflow: "hidden", background: FLO_COLORS.canvasBg, borderRight: HAIRLINE,
             boxSizing: "border-box",
           }}>
        {geometry.lanes.map((lane) => (
          <GutterLane key={lane.k} lane={lane} view={view} t={t} fonts={fonts} />
        ))}
      </div>
      <div data-flo-corner
           style={{
             position: "absolute", left: 0, top: 0, width: FLO_GUTTER_W, height: FLO_RULER_H,
             background: FLO_COLORS.canvasBg, borderRight: HAIRLINE, borderBottom: HAIRLINE,
             boxSizing: "border-box",
           }} />
    </>
  );
}
